import { Injectable, signal } from '@angular/core';

export type UiLoadingModalVariant = 'loading' | 'success' | 'error' | 'warning';

export interface UiLoadingModalOptions {
  variant?: UiLoadingModalVariant;
  title?: string;
  message?: string;
  /** Label del botón principal de acción. */
  confirmLabel?: string;
  /** Muestra el botón principal de acción (defecto: true en variantes no-loading). */
  showButton?: boolean;
  /** Muestra un botón secundario de cancelación junto al botón principal. */
  showCancelButton?: boolean;
  /** Label del botón secundario (defecto: 'Cancelar'). */
  cancelLabel?: string;
  /** Aplica estilo de peligro (rojo) al botón principal. Útil para acciones destructivas. */
  confirmDanger?: boolean;
  /** Si es true, el backdrop no es clickeable y se bloquea scroll/teclado. */
  blockUi?: boolean;
}

/**
 * Servicio de estado UI para el LoadingModal.
 * No contiene lógica de dominio: únicamente señales de presentación.
 */
@Injectable({ providedIn: 'root' })
export class UiLoadingModalService {
  readonly isOpen         = signal(false);
  readonly variant        = signal<UiLoadingModalVariant>('loading');
  readonly title          = signal('Cargando…');
  readonly message        = signal('');
  readonly confirmLabel   = signal('Aceptar');
  readonly showButton     = signal(true);
  readonly showCancelButton = signal(false);
  readonly cancelLabel    = signal('Cancelar');
  readonly confirmDanger  = signal(false);
  readonly blockUi        = signal(true);

  open(options?: UiLoadingModalOptions): void {
    const v = options?.variant ?? 'loading';
    const defaultLabel =
      v === 'error'   ? 'Cerrar'     :
      v === 'warning' ? 'Entendido'  : 'Aceptar';

    this.variant.set(v);
    this.title.set(options?.title ?? 'Cargando…');
    this.message.set(options?.message ?? '');
    this.confirmLabel.set(options?.confirmLabel ?? defaultLabel);
    this.showButton.set(options?.showButton ?? true);
    this.showCancelButton.set(options?.showCancelButton ?? false);
    this.cancelLabel.set(options?.cancelLabel ?? 'Cancelar');
    this.confirmDanger.set(options?.confirmDanger ?? false);
    this.blockUi.set(options?.blockUi ?? v === 'loading');
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  updateTitle(title: string): void {
    this.title.set(title);
  }

  updateMessage(message: string): void {
    this.message.set(message);
  }

  updateVariant(variant: UiLoadingModalVariant): void {
    this.variant.set(variant);
  }
}
